import { API_BASE_URL } from '../constants';
import { showErrorToast } from '../utils';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Get auth token from localStorage - check for super admin token if:
    // 1. Endpoint is for super admin routes, OR
    // 2. Endpoint is for tenants and super admin token exists, OR
    // 3. Super admin token exists (use it for all API calls since tenant pages are super-admin-only)
    const isSuperAdminEndpoint = endpoint.startsWith('/super-admin');
    const isTenantEndpoint = endpoint.startsWith('/tenants');
    const hasSuperAdminToken = !!localStorage.getItem('super_admin_token');

    // If super admin token exists, use it for all API calls
    // This ensures tenant sub-pages (documents, bank-details, feature-defaults) work correctly
    const shouldUseSuperAdminToken = isSuperAdminEndpoint || (isTenantEndpoint && hasSuperAdminToken) || hasSuperAdminToken;

    const token = shouldUseSuperAdminToken
      ? localStorage.getItem('super_admin_token')
      : localStorage.getItem('auth_token');

    // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
    const isFormData = options.body instanceof FormData;

    const config = {
      headers: {
        // Only set Content-Type for non-FormData requests
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 Unauthorized - token might be expired or session invalidated
      // BUT: Skip token refresh for auth endpoints (login/register) where 401 means invalid credentials
      if (response.status === 401) {
        // Check if this is an auth endpoint (login/register) - these should not trigger token refresh
        const isAuthEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/register') || endpoint.includes('/super-admin/auth/login');

        // Check if this is a super admin request
        // If super admin token exists, treat all requests as super admin requests
        const hasSuperAdminTokenForRequest = !!localStorage.getItem('super_admin_token');
        const isSuperAdminRequest = endpoint.startsWith('/super-admin') ||
          (endpoint.startsWith('/tenants') && hasSuperAdminTokenForRequest) ||
          hasSuperAdminTokenForRequest;

        // Only attempt token refresh if:
        // 1. This is NOT an auth endpoint (login/register)
        // 2. There's actually a token present (user was previously authenticated)
        // 3. This is NOT a super admin request (super admin tokens are handled separately)
        if (!isAuthEndpoint && token && !isSuperAdminRequest) {
          // Check if this is a session invalidation (permission change) or token expiry
          const responseData = await response.json().catch(() => ({}));

          // Try to refresh token first, even for session-related errors
          // This makes the app more resilient to backend Redis/circuit breaker issues
          try {
            const authService = (await import('./authService')).default;
            const newToken = await authService.refreshToken();

            // Retry the request with new token
            config.headers['Authorization'] = `Bearer ${newToken}`;
            const retryResponse = await fetch(url, config);

            if (!retryResponse.ok) {
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }

            const data = await retryResponse.json();
            return data;
          } catch (refreshError) {
            // Refresh failed - check if this is a real session revocation or just a temporary issue
            // If the message indicates a real permission change, show that message
            if (responseData.message && responseData.message.includes('permission change')) {
              showErrorToast('Your session has been terminated due to permission changes. Please login again.');
            } else {
              showErrorToast('Session expired. Please login again.');
            }

            // Force logout
            if (isSuperAdminRequest) {
              localStorage.removeItem('super_admin_token');
              localStorage.removeItem('super_admin_refresh_token');
              localStorage.removeItem('super_admin_data');
              window.location.href = '/super-admin/login';
            } else {
              localStorage.removeItem('auth_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user_data');
              window.location.href = '/login';
            }
            return;
          }
        }
        // If it's an auth endpoint or no token exists, let the error propagate normally
        // This allows login/register error handling to work correctly
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Preserve specific error details from backend response
        let errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;

        // Handle case where message is an object or array
        if (typeof errorMessage === 'object') {
          try {
            errorMessage = JSON.stringify(errorMessage);
          } catch (e) {
            errorMessage = 'An error occurred';
          }
        }

        const error = new Error(errorMessage);
        // Attach additional error details if available
        if (errorData.error || errorData.details) {
          error.details = errorData.error || errorData.details;
        }
        // Normalize axios-style response shape so downstream error handling can parse details
        error.response = {
          status: response.status,
          data: errorData
        };
        // Preserve validation errors array if present
        if (Array.isArray(errorData.errors)) {
          error.errors = errorData.errors;
        }
        throw error;
      }

      // Check for new access token in response headers (permission refresh)
      const newAccessToken = response.headers.get('X-New-Access-Token');
      const refreshReason = response.headers.get('X-Token-Refresh-Reason');

      if (newAccessToken && refreshReason === 'permissions-updated') {
        // Update token in localStorage
        localStorage.setItem('auth_token', newAccessToken);

        // Decode token to get updated permissions
        try {
          const tokenPayload = JSON.parse(atob(newAccessToken.split('.')[1]));
          const updatedPermissions = tokenPayload.permissions;

          // Update user data in localStorage with new permissions
          const userDataStr = localStorage.getItem('user_data');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            userData.permissions = updatedPermissions;
            localStorage.setItem('user_data', JSON.stringify(userData));
          }

          // Dispatch custom event to notify auth context about permission update
          window.dispatchEvent(new CustomEvent('permissions-updated', {
            detail: { permissions: updatedPermissions }
          }));

          console.info('Token refreshed with updated permissions');
        } catch (decodeError) {
          console.error('Error decoding new token:', decodeError);
        }
      }

      // Handle response - check if it's JSON or other format
      const contentType = response.headers.get('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // For non-JSON responses, try to parse as text
        const text = await response.text();
        try {
          data = JSON.parse(text);
        } catch {
          data = { success: response.ok, data: text };
        }
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // GET request
  async get(endpoint, params = {}, signal) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET', signal });
  }

  // POST request
  async post(endpoint, data, signal) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      signal
    });
  }

  // PUT request
  async put(endpoint, data, signal) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal
    });
  }

  // DELETE request
  async delete(endpoint, signal) {
    return this.request(endpoint, { method: 'DELETE', signal });
  }

  // PATCH request
  async patch(endpoint, data, signal) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      signal
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
